/**
 * Types and API helpers for seasons. Backend contract (all responses are
 * { status, data }):
 *   GET    /api/seasons?venueId=<int>
 *   GET    /api/seasons/defaults?venueId=<int>
 *   PUT    /api/seasons/defaults/:venueId
 *   POST   /api/seasons
 *   GET    /api/seasons/:id
 *   PATCH  /api/seasons/:id
 *   DELETE /api/seasons/:id
 *   GET    /api/seasons/:id/standings
 *   GET    /api/seasons/public?venueId=<int>
 *   GET    /api/seasons/public/:id/standings
 *   GET    /api/seasons/public/venue/:venueId/current
 */
import api from '../../utils/api';
import type { LeftEarlyRule, SeasonRuleLike, Tiebreaker } from '../../utils/seasons';

export type ScoringMode = 'TOTAL_POINTS' | 'PLACEMENT_POINTS';

export interface Season extends SeasonRuleLike {
  leftEarly: LeftEarlyRule;
  tiebreakers: Tiebreaker[];
  id: number;
  venueId: number;
  name: string;
  startDate: string;
  endDate: string;
  isPublic: boolean;
  venue: { id: number; name: string };
}

export interface SeasonInput {
  name: string;
  venueId: number;
  startDate: string;
  endDate: string;
  scoringMode: ScoringMode;
  placementPoints: number[];
  bestNights: number | null;
  attendanceBonus: number;
  leftEarly: LeftEarlyRule;
  tiebreakers: Tiebreaker[];
  isPublic: boolean;
}

// The rule fields a venue can set as defaults for its new seasons.
export type SeasonRuleInput = Pick<
  SeasonInput,
  'scoringMode' | 'placementPoints' | 'bestNights' | 'attendanceBonus' | 'leftEarly' | 'tiebreakers'
>;

export interface VenueSeasonDefaults {
  venue: { id: number; name: string };
  values: SeasonRuleInput;
  // 'venue' when the venue sets the value, 'default' for the built-in value.
  source: Record<keyof SeasonRuleInput, 'venue' | 'default'>;
}

export interface SeasonNight {
  eventId: string;
  date: string;
  name?: string | null;
  teamCount: number;
}

export interface StandingNight {
  eventId: string;
  date?: string;
  liveTeamId?: string;
  nameUsed?: string;
  grandTotal?: number;
  placement: number | null;
  leftEarly: boolean;
  // The place from the score when the team left (placement mode, KEEP_POINTS).
  leftEarlyPlace?: number | null;
  points: number;
  counted: boolean;
}

export interface StandingRow {
  key?: string;
  canonicalTeamId?: number | null;
  liveTeamId?: string | null;
  name: string;
  unlinked?: boolean;
  rank: number;
  total: number;
  nightScore: number;
  attendancePoints: number;
  nightsAttended: number;
  nightsSkipped?: number;
  nightsLeftEarly?: number;
  wins: number;
  bestNight?: number;
  tiebreakApplied?: boolean;
  nights: StandingNight[];
}

export interface SeasonStandingsResponse {
  season: Season;
  totalNights: number;
  unlinkedCount?: number;
  nights: SeasonNight[];
  rows: StandingRow[];
}

export async function fetchSeasons(venueId?: number | null): Promise<Season[]> {
  const q = venueId ? `?venueId=${venueId}` : '';
  const res = await api.get(`/api/seasons${q}`);
  return (res.data.data?.seasons ?? []) as Season[];
}

export async function fetchVenueSeasonDefaults(venueId: number): Promise<VenueSeasonDefaults> {
  const res = await api.get(`/api/seasons/defaults?venueId=${venueId}`);
  return res.data.data as VenueSeasonDefaults;
}

// A null field clears that venue default.
export async function saveVenueSeasonDefaults(
  venueId: number,
  input: { [K in keyof SeasonRuleInput]?: SeasonRuleInput[K] | null },
): Promise<VenueSeasonDefaults> {
  const res = await api.put(`/api/seasons/defaults/${venueId}`, input);
  return res.data.data as VenueSeasonDefaults;
}

export async function createSeason(input: SeasonInput): Promise<Season> {
  const res = await api.post('/api/seasons', input);
  return res.data.data.season as Season;
}

export async function updateSeason(id: number, input: Partial<SeasonInput>): Promise<Season> {
  const res = await api.patch(`/api/seasons/${id}`, input);
  return res.data.data.season as Season;
}

export async function deleteSeason(id: number): Promise<void> {
  await api.delete(`/api/seasons/${id}`);
}

export async function fetchSeasonStandings(id: number): Promise<SeasonStandingsResponse> {
  const res = await api.get(`/api/seasons/${id}/standings`);
  return res.data.data as SeasonStandingsResponse;
}

export async function fetchPublicSeasons(): Promise<Pick<Season, 'id' | 'name' | 'startDate' | 'endDate' | 'venue'>[]> {
  const res = await api.get('/api/seasons/public');
  return res.data.data?.seasons ?? [];
}

export async function fetchPublicSeasonStandings(id: number): Promise<SeasonStandingsResponse> {
  const res = await api.get(`/api/seasons/public/${id}/standings`);
  return res.data.data as SeasonStandingsResponse;
}

// Pull a readable message out of an API error, with a fallback.
export function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || fallback;
}
